import { apiClient as api } from './api';
import {
  MusicPromptSession,
  MusicPromptIteration,
  SessionStartRequest,
  SessionStartResponse,
  SessionStatusResponse,
  GenerateMusicIterationRequest,
  IterationResponse,
  MusicAgentType,
  ComprehensivePromptRequest,
  MusicPromptGenerateResponse,
  GenrePromptRequest,
  MoodPromptRequest,
  ArrangementPromptRequest
} from '../models/musicPrompt';

export class MusicPromptService {
  private static readonly BASE_URL = '/api/music-prompt';
  private static readonly STORAGE_KEY = 'music_prompt_sessions_v2';

  // API methods
  static async startSession(
    concept: string, 
    sessionType: string = 'Standard',
    options?: {
      title?: string;
      genre?: string;
      purpose?: string;
      targetDuration?: number;
      maxResponseTokens?: number;
      temperature?: number;
    }
  ): Promise<SessionStartResponse> {
    const request: SessionStartRequest = {
      concept: concept.trim(),
      title: options?.title?.trim() || undefined,
      genre: options?.genre || undefined,
      purpose: options?.purpose || undefined,
      targetDuration: options?.targetDuration || undefined,
      maxResponseTokens: options?.maxResponseTokens || 2000,
      temperature: options?.temperature || 0.7
    };
    
    const response = await api.post<SessionStartResponse>(`${this.BASE_URL}/session/start`, request);
    
    if (!response.data.success || !response.data.sessionId) {
      throw new Error(response.data.errors?.join(', ') || 'Failed to start session');
    }
    
    return response.data;
  }

  static async getSession(sessionId: string): Promise<SessionStatusResponse> {
    const response = await api.get(`${this.BASE_URL}/session/${sessionId}`);
    return response.data;
  }

  static async generateIteration(sessionId: string, request: GenerateMusicIterationRequest): Promise<MusicPromptGenerateResponse> {
    // Build the complete API request with all required fields
    const apiRequest = {
      specificGenre: request.specificGenre || 'contemporary',
      subGenres: request.subGenres || [],
      eraOrPeriod: request.eraOrPeriod || '',
      genreSpecificTechniques: request.genreSpecificTechniques || '',
      additionalInstructions: request.additionalInstructions || '',
      maxTokens: request.maxTokens || 2000,
      temperature: request.temperature || 0.7
    };

    // Use genre-prompt endpoint as the primary endpoint for all iterations
    const endpoint = `${this.BASE_URL}/session/${sessionId}/genre-prompt`;
    
    const response = await api.post<MusicPromptGenerateResponse>(endpoint, apiRequest);
    
    if (!response.data.success) {
      throw new Error(response.data.errors?.join(', ') || 'Failed to generate prompt');
    }
    
    return response.data;
  }

  static async bulkIterate(sessionId: string, iterationCount: number = 3): Promise<any> {
    const response = await api.post(`${this.BASE_URL}/session/${sessionId}/bulk-iterate`, {
      iterationCount,
      temperature: 0.7
    });
    return response.data;
  }

  // Local storage management
  static saveToLocalStorage(sessions: MusicPromptSession[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessions));
    } catch (error) {
      console.error('Error saving to local storage:', error);
    }
  }

  static getFromLocalStorage(): MusicPromptSession[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const sessions = JSON.parse(stored);
      // Convert date strings back to Date objects
      return sessions.map((session: any) => ({
        ...session,
        createdAt: new Date(session.createdAt),
        lastUpdatedAt: new Date(session.lastUpdatedAt),
        iterations: session.iterations.map((iter: any) => ({
          ...iter,
          timestamp: new Date(iter.timestamp)
        }))
      }));
    } catch (error) {
      console.error('Error loading from local storage:', error);
      return [];
    }
  }

  static saveSession(session: MusicPromptSession): void {
    const sessions = this.getFromLocalStorage();
    const existingIndex = sessions.findIndex(s => s.id === session.id);
    
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.unshift(session);
    }
    
    this.saveToLocalStorage(sessions);
  }

  static loadSessions(): MusicPromptSession[] {
    return this.getFromLocalStorage();
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.getFromLocalStorage();
    const filtered = sessions.filter(s => s.id !== sessionId);
    this.saveToLocalStorage(filtered);
  }

  static updateSession(updatedSession: MusicPromptSession): void {
    const sessions = this.getFromLocalStorage();
    const index = sessions.findIndex(s => s.id === updatedSession.id);
    
    if (index >= 0) {
      sessions[index] = updatedSession;
      this.saveToLocalStorage(sessions);
    }
  }

  // Utility methods
  static exportSession(session: MusicPromptSession): void {
    const content = JSON.stringify(session, null, 2);
    this.downloadFile(content, `music-prompt-${session.id}.json`, 'application/json');
  }

  static async importSession(file: File): Promise<MusicPromptSession> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const session = JSON.parse(content);
          
          // Convert date strings to Date objects
          session.createdAt = new Date(session.createdAt);
          session.lastUpdatedAt = new Date(session.lastUpdatedAt);
          session.iterations = session.iterations.map((iter: any) => ({
            ...iter,
            timestamp: new Date(iter.timestamp)
          }));
          
          // Generate new ID to avoid conflicts
          session.id = `imported_${Date.now()}`;
          
          resolve(session);
        } catch (error) {
          reject(new Error('Invalid session file format'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  static async copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
      } catch (error) {
        console.error('Failed to copy text:', error);
        throw error;
      } finally {
        textArea.remove();
      }
    }
  }

  static downloadFile(content: string, filename: string, contentType: string): void {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}